import { FilterDefs, multiFilter, NestedOption, treeFilter } from "@homebound/beam";
import {
  BidPackageStatus,
  CostCodeBidSummariesFilter,
  CostCodeBidSummaryFlag,
  CostCodeBidSummaryStatus,
  ReadyPlanStatus,
} from "src/generated/graphqlTypes";
import { BidPackagesPageV2MetadataQuery } from "./BidPackagesPageV2.generated";
import {
  bidPackageStatusRank,
  CostCodeBidSummaryGroup,
  GroupFlag,
  productOfferingsNotCovered,
  uncoveredFlags,
} from "./utils";

/** A status or flag a group can show, or one of the two nodes that only group them in the tree. */
export type BidCoverageValue = CostCodeBidSummaryStatus | GroupFlag | "ALL_ISSUES" | "WITH_WARNINGS";
type BidCoverageOption = NestedOption<{ value: BidCoverageValue; label: string }>;

type StatusOption = NestedOption<{ value: BidPackageStatus; label: string }>;

export type BidPackagesPageFilter = {
  bidCoverage?: BidCoverageValue[];
  productOfferings?: string[];
  status?: BidPackageStatus[];
  costCodes?: string[];
};

const bidCoverageOptions: BidCoverageOption[] = [
  {
    value: "ALL_ISSUES",
    label: "All Issues",
    children: [
      {
        value: CostCodeBidSummaryStatus.Missing,
        label: "Missing",
        children: [
          { value: CostCodeBidSummaryFlag.MissingBid, label: "Missing Bid" },
          { value: CostCodeBidSummaryFlag.MissingRequiredBid, label: "Missing Required Bid" },
          { value: productOfferingsNotCovered, label: "Product Offerings Not Covered" },
        ],
      },
      {
        value: CostCodeBidSummaryStatus.Incomplete,
        label: "Incomplete",
        children: [
          { value: CostCodeBidSummaryFlag.MissingCosts, label: "Missing Costs" },
          { value: CostCodeBidSummaryFlag.MissingOptions, label: "Missing Options" },
          { value: CostCodeBidSummaryFlag.ExcludedMaterial, label: "Excluded Material" },
          { value: CostCodeBidSummaryFlag.ExcludedTasks, label: "Excluded Tasks" },
        ],
      },
      {
        value: "WITH_WARNINGS",
        label: "With Warnings",
        children: [
          { value: CostCodeBidSummaryFlag.CostMissingScope, label: "Cost Missing Scope" },
          { value: CostCodeBidSummaryFlag.MissingTrade, label: "Missing Trade" },
        ],
      },
    ],
  },
  { value: CostCodeBidSummaryStatus.InProgress, label: "In Progress" },
  { value: CostCodeBidSummaryStatus.Complete, label: "Complete" },
];

type Community = BidPackagesPageV2MetadataQuery["community"];
type BidPackageStatusDetail = BidPackagesPageV2MetadataQuery["enumDetails"]["bidPackageStatus"][number];
type CostCode = BidPackagesPageV2MetadataQuery["costCodes"][number];

/** The product offerings are the community's active ones, and the statuses list in the order packages sort in. */
export function createFilterDefs(
  community: Community,
  bidPackageStatuses: BidPackageStatusDetail[],
  costCodes: CostCode[],
): FilterDefs<BidPackagesPageFilter> {
  const productOfferings = community.productOfferings.filter((po) => po.status.code === ReadyPlanStatus.Active);
  return {
    bidCoverage: treeFilter({
      label: "Bid Coverage",
      filterBy: "leaf",
      options: bidCoverageOptions,
      getOptionValue: (o) => o.value,
      getOptionLabel: (o) => o.label,
    }),
    productOfferings: multiFilter({
      label: "Product Offerings",
      options: productOfferings.sortBy((po) => po.name),
      getOptionValue: (o) => o.id,
      getOptionLabel: (o) => o.name,
    }),
    status: treeFilter({
      label: "Status",
      filterBy: "leaf",
      options: statusOptions(bidPackageStatuses),
      getOptionValue: (o) => o.value,
      getOptionLabel: (o) => o.label,
    }),
    costCodes: multiFilter({
      label: "Cost Code",
      options: costCodes.sortBy((c) => c.name),
      getOptionValue: (o) => o.id,
      getOptionLabel: (o) => o.name,
    }),
  };
}

function statusOptions(statuses: BidPackageStatusDetail[]): StatusOption[] {
  const [awarded, others] = statuses.partition((s) => awardedStatuses.includes(s.code));
  return [
    {
      value: "ALL_AWARDED" as BidPackageStatus,
      label: "All Awarded",
      children: awarded.sortBy((s) => awardedStatuses.indexOf(s.code)).map((s) => ({ value: s.code, label: s.name })),
    },
    ...others.sortBy((s) => bidPackageStatusRank.indexOf(s.code)).map((s) => ({ value: s.code, label: s.name })),
  ];
}

const awardedStatuses = [BidPackageStatus.Awarded, BidPackageStatus.Amending];

// Only real statuses and flags go to the BE (i.e. not the nodes that group the tree)
const statusValues: BidCoverageValue[] = Object.values(CostCodeBidSummaryStatus);
const flagValues: BidCoverageValue[] = [...Object.values(CostCodeBidSummaryFlag), productOfferingsNotCovered];

export function mapToFilter(filter: BidPackagesPageFilter, projectId: string): CostCodeBidSummariesFilter {
  const picked = filter.bidCoverage ?? [];
  const status = picked.filter((v): v is CostCodeBidSummaryStatus => statusValues.includes(v));
  const flags = picked
    .filter((v): v is GroupFlag => flagValues.includes(v))
    // Partial coverage is an FE-only flag derived from the missing bid flags, so those are what the BE can filter on
    .flatMap((f) => (f === productOfferingsNotCovered ? uncoveredFlags : [f]))
    .unique();
  return {
    projects: [projectId],
    productOfferings: filter.productOfferings,
    costCodes: filter.costCodes,
    bidPackageStatus: filter.status,
    status: status.nonEmpty ? status : undefined,
    flags: flags.nonEmpty ? flags : undefined,
  };
}

/**
 * Whether the group as shown matches what's picked. Its chip shows a picked status, or one of its banners
 * shows a picked flag. Nothing picked keeps every group.
 */
export function matchesBidCoverage(
  { status, flags }: Pick<CostCodeBidSummaryGroup, "status" | "flags">,
  picked: BidCoverageValue[] = [],
): boolean {
  return picked.isEmpty || picked.includes(status.code) || flags.some((f) => picked.includes(f));
}
